import { Plus } from 'lucide-react';
import React from 'react';

import { Button } from '@web/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@web/components/ui/card';
import { Filter, ViewFilters } from './view-filters';

type ViewContainerProps<F extends Filter[]> = {
  title?: string;
  addButtonLabel?: string;
  onAddButtonClick?: () => void | Promise<void>;
  children: React.ReactNode;
  filters?: F;
  onFilterChange?: (
    newSearch: Record<string, string | number | boolean | object | undefined>,
  ) => void;
};

export function ViewContainer<F extends Filter[]>({
  title,
  addButtonLabel,
  onAddButtonClick,
  children,
  filters,
  onFilterChange,
}: Readonly<ViewContainerProps<F>>) {
  return (
    <>
      {/* 
        Desktop / Tablet (md+) Layout 
        - Uses original design 
      */}
      <div className="mt-10 hidden h-[calc(100vh-theme(spacing.10))] w-full flex-col p-0 pb-20 md:flex">
        {/* Optional header */}
        {title && (
          <Card className="mb-8 p-6">
            <CardHeader className="flex w-full flex-row items-center justify-between p-0">
              <CardTitle>{title}</CardTitle>

              {/* Only show the Add button if label and callback are provided */}
              {addButtonLabel && onAddButtonClick && (
                <Button
                  variant="outline"
                  onClick={() => void onAddButtonClick()}
                  className="shadow-floating"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  {addButtonLabel}
                </Button>
              )}
            </CardHeader>
          </Card>
        )}

        {/* Main content area */}
        <Card className="flex h-full flex-col overflow-hidden p-0">
          {filters && onFilterChange && (
            <CardHeader className="mb-2 p-4 px-2 shadow-sm">
              <ViewFilters filters={filters} onFilterChange={onFilterChange} />
            </CardHeader>
          )}
          <CardContent className="scrollbar-custom w-full flex-1 overflow-y-auto p-0 px-2">
            {children}
          </CardContent>
        </Card>
      </div>

      {/* 
        Mobile Layout (below md)
        - Improved for better scrolling and touch interaction
      */}
      <div className="relative flex h-[calc(100dvh_-_var(--mobile-header-height))] w-full flex-col overflow-hidden md:hidden">
        <Card className="flex h-full w-full flex-col overflow-hidden border-0 p-0 shadow-none">
          {/* Mobile-optimized sticky header with improved positioning */}
          <CardHeader className="bg-card sticky top-0 z-20 flex min-h-[65px] w-full items-center overflow-visible border-b px-4 pb-3 pt-3 shadow-sm">
            <div className="flex w-full items-center justify-between">
              {title && (
                <CardTitle className="mr-2 max-w-[60%] truncate text-lg">
                  {title}
                </CardTitle>
              )}

              {/* Action buttons row - always visible and compact */}
              <div className="ml-auto flex shrink-0 items-center gap-2">
                {/* Filters button with improved positioning - always visible */}
                {filters && onFilterChange && (
                  <div className="relative z-20 shrink-0">
                    <ViewFilters
                      filters={filters}
                      onFilterChange={onFilterChange}
                      mobileView={true}
                    />
                  </div>
                )}

                {/* Add button in header - circular icon on mobile */}
                {onAddButtonClick && (
                  <Button
                    variant="default"
                    size="icon"
                    onClick={() => void onAddButtonClick()}
                    className="h-8 w-8 shrink-0 rounded-full shadow-sm md:h-9 md:w-auto md:rounded-md md:px-3"
                    aria-label={addButtonLabel ?? 'Add'}
                  >
                    <Plus className="h-4 w-4" />
                    <span className="sr-only md:not-sr-only md:ml-1">
                      {addButtonLabel ?? 'Add'}
                    </span>
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>

          {/* Main content area - improved for mobile scrolling */}
          <CardContent className="scrollbar-custom flex-1 overflow-hidden p-0">
            {/* Main scrollable container with improved touch handling */}
            <div
              className="h-full overflow-y-auto overscroll-contain pb-16"
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              {/* Container for horizontal scroll when needed, with auto width detection */}
              <div className="min-h-full w-full overflow-x-auto">
                {/* Content wrapper with proper padding and no forced width */}
                <div className="w-full px-3 pt-4 sm:px-4">{children}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* We don't need the floating button anymore as we always show the add button in the header */}
      </div>
    </>
  );
}
